/* eslint-disable default-case */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../../elements/Box';
import Button from '../../../elements/Button';
import FlightTypeFilter from '../../../controls/SecondaryFilters/FlightTypeFilter';
import FullyVaccinatedFilter from '../../../controls/SecondaryFilters/FullyVaccinatedFilter';

const SecondaryFilters = (
  {
    containerSettings,
    containerFiltersSetting,
    availableFilters,
    dateRange,
    onClearFiltersSecondary,
    clearAllSettings,
    clearAllColumnSettings,
    flightType,
    locations,
    flightTypeSettings,
    flightTypeColumnSettings,
    showClearButton = false,
    FullyVaccinatedColumnSettings,
    fullyVaccinatedSettings,
    fullyVaccinated
  },
  ref
) => {
  const { translate } = React.useContext(Context);
  const elementsWrappers = {
    FlightType: (e) => (
      <Box
        key="FlightType"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightTypeColumnSettings}
      >
        {e}
      </Box>
    ),
    FullyVaccinated: (e) => (
      <Box
        key="FlightType"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...FullyVaccinatedColumnSettings}
      >
        {e}
      </Box>
    )
  };

  const elements = {
    FlightType: <FlightTypeFilter {...flightTypeSettings} {...flightType} />,
    FullyVaccinated: <FullyVaccinatedFilter {...fullyVaccinatedSettings} {...fullyVaccinated} />
  };

  /**
   * It is necessary to create the payload of only the active filters,
   * because clearAll only affects the secondary filters, never the primary ones.
   *
   * IMPORTANT: As new filters are added, the way in which said
   * filters are cleaned must also be added.
   */
  const handleClear = () => {
    let newPayload = {};
    setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    onClearFiltersSecondary(newPayload);
  };

  const filtersChanged = React.useMemo(() => {
    let filtersChange = !!(flightType.isChanged || fullyVaccinated.isChanged);
    return filtersChange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flightType.isChanged,
    fullyVaccinated.isChanged,
    availableFilters,
    locations.origin.value,
    locations.destination.value,
    dateRange.value.start,
    dateRange.value.end
  ]);

  const ClearButton = () => (
    <Box floatLeft {...clearAllColumnSettings}>
      <Button
        onClick={handleClear}
        disabled={!filtersChanged}
        cursor={!filtersChanged ? 'default' : 'pointer'}
        dataTestId="clearAllBtn"
        ariaLabel={translate('cmp-secondary-filter-clear-button-accessibility-message', 'Clear')}
        {...clearAllSettings}
      >
        {translate('cmp-secondary-filter-clear', 'Clear')}
      </Button>
    </Box>
  );

  const firstElementRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focusFirstElement: () => {
      setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    }
  }));

  return (
    <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
      <Box display="inline-block" {...containerFiltersSetting}>
        {availableFilters.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);
          if (index === 0) {
            const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
            return wrapper(temporalElement);
          } else {
            return element;
          }
        })}
        {showClearButton && <ClearButton />}
      </Box>
    </Box>
  );
};

SecondaryFilters.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['FlightType', 'Route'])),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),

  FullyVaccinatedColumnSettings: PropTypes.objectOf(PropTypes.any),
  fullyVaccinatedSettings: PropTypes.objectOf(PropTypes.any),
  fullyVaccinated: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),

  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),

  onClear: PropTypes.func,
  loading: PropTypes.bool
};

SecondaryFilters.defaultProps = {
  loading: false,
  availableFilters: ['FlightType', 'DatePicker'],
  showLabels: false,
  containerSettings: {},
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true
  },
  clearAllColumnSettings: {},

  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  locations: { origin: {}, destination: {} },
  flightType: {},
  onClear: null,
  FullyVaccinatedColumnSettings: {},
  fullyVaccinatedSettings: {},
  fullyVaccinated: {}
};

export default React.forwardRef(SecondaryFilters);
